"use client";

import Image from "next/image";
import { ChevronRight } from "lucide-react";
export const AppsShowcaseSection = () => {
  const apps = [
    {
      id: "loyalty",
      name: "Loyalty & Referrals",
      // Was "To build the reliable tools that power merchant growth." — the
      // Mission statement from MissionVisionSection, pasted here by mistake. It
      // described the company, not the app, and the same sentence appeared
      // twice on the page. The two sibling cards describe what their app does;
      // this one now does the same.
      description: "Turn one-time buyers into repeat customers.",
      image: "/images/covers/LR-cover.webp",
      link: "https://apps.shopify.com/keystone-loyalty-rewards?utm_source=website&utm_medium=organic&utm_campaign=homepage",
      hoverGradient:
        "linear-gradient(149.4deg, #EEFFFB 6.54%, #3DEDD0 40.27%, #C17BFF 70.76%)",
    },
    {
      id: "product-options",
      name: "Product Options",
      description: "Create limitless product choices and variants.",
      image: "/images/covers/PO-cover.webp",
      link: "https://apps.shopify.com/keystone-product-options-variants?utm_source=website&utm_medium=organic&utm_campaign=homepage",
      hoverGradient:
        "linear-gradient(318.5deg, #BBE1E6 20.26%, #FCFB00 54.99%, #87D500 79.02%)",
    },
    {
      id: "ai-seo",
      // "AI SEO Optimize for LLMs" ran two phrases together and doubled as the
      // card's image alt text; the sibling cards are plain product names.
      name: "AI SEO",
      description: "Get your Shopify store discovered by AI.",
      image: "/images/covers/AI-cover.webp",
      link: "https://apps.shopify.com/key-ai-seo-1?utm_source=website&utm_medium=organic&utm_campaign=homepage",
      hoverGradient:
        "linear-gradient(79.19deg, #DE3859 38.6%, #FFA804 67.75%, #BBE1E6 105.56%)",
    },
  ];

  return (
    <section
      id="apps"
      className="pt-24 pb-32 relative overflow-hidden bg-[#212121]"
    >
      <div className="container mx-auto px-6 relative z-10 max-w-6xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-[32px] md:text-[48px] font-medium text-[#f8f7f4] text-center tracking-[-0.03em] leading-[40px] md:leading-[56px] mb-4">
            Scale smarter with Keystone
          </h2>
          <p className="text-[16px] md:text-[18px] font-light text-[#c4c4c4] text-center tracking-[-0.002em] leading-[24px] md:leading-[28px]">
            Loyalty, Customization, and AI SEO all in one place.
          </p>
        </div>

        {/* Apps Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {apps.map((app) => (
            <a
              key={app.id}
              href={app.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative cursor-pointer"
            >
              {/* Gradient glow behind card on hover */}
              <div
                className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  width: "350px",
                  height: "300px",
                  borderRadius: "61px",
                  background: app.hoverGradient,
                  transform: "rotate(-45deg)",
                  filter: "blur(150px)",
                  top: "35%",
                  left: "50%",
                  translate: "-50% -50%",
                  zIndex: 0,
                }}
              />

              {/* Card */}
              <div
                className="relative rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-500 cursor-pointer group-hover:-translate-y-[22px]"
                style={{
                  background: "#1E1E1E",
                  border: "2px solid rgba(222, 224, 231, 0.1)",
                }}
              >
                {/* Image Section - 277px height from Figma */}
                <div
                  className="relative overflow-hidden"
                  style={{ height: "277px" }}
                >
                  <Image
                    src={app.image}
                    alt={app.name}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 448px"
                    className="object-cover"
                  />
                </div>

                {/* Content Section */}
                <div
                  className="flex flex-col flex-1"
                  style={{ padding: "32px 24px 24px", gap: "32px" }}
                >
                  {/* Card Contents */}
                  <div
                    className="flex flex-col flex-1"
                    style={{ padding: "0 8px", gap: "12px" }}
                  >
                    <h3
                      className="font-medium text-[#F8F7F4]"
                      style={{
                        fontSize: "20px",
                        lineHeight: "24px",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {app.name}
                    </h3>
                    <p
                      className="font-medium text-[#A4A4A4]"
                      style={{
                        fontSize: "16px",
                        lineHeight: "24px",
                        letterSpacing: "-0.002em",
                      }}
                    >
                      {app.description}
                    </p>
                  </div>

                  {/* Button */}
                  <button
                    className="flex items-center w-full cursor-pointer bg-[#1E1E1E] border border-[rgba(96,96,96,0.4)] rounded-[10px] shadow-[0px_0px_13.3px_7px_#1A1A1A,inset_0px_0px_0px_0px_transparent] hover:border-[#606060] hover:shadow-[0px_0px_0px_0px_transparent,inset_0px_0px_8.7px_1px_#bfbeb8] transition-[box-shadow,border-color] duration-500 ease-in-out"
                    style={{ padding: "16px 16px 18px" }}
                  >
                    <span
                      className="font-medium text-[#BFBEB8]"
                      style={{
                        fontSize: "16px",
                        lineHeight: "20px",
                        letterSpacing: "-0.009em",
                      }}
                    >
                      Learn more
                    </span>
                    <ChevronRight
                      className="w-5 h-5 ml-auto text-[#BFBEB8]"
                      strokeWidth={1.5}
                    />
                  </button>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
